import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    Alert,
    Stepper,
    Step,
    StepLabel,
    CircularProgress,
    IconButton,
    InputAdornment,
    Chip,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Snackbar,
    Tooltip
} from '@mui/material';
import { Visibility, VisibilityOff, Add, AdminPanelSettings, Person, Edit, Delete } from '@mui/icons-material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Logo from '../assets/logo.png';
import authService from '../services/auth';
import { userService } from '../services/api';

const Login = ({ onAuthChange }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [setupRequired, setSetupRequired] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Login state
    const [loginForm, setLoginForm] = useState({
        username: '',
        password: '',
    });
    const [showPassword, setShowPassword] = useState(false);

    // Root setup state
    const [setupForm, setSetupForm] = useState({
        username: '',
        password: '',
        password_confirm: '',
        email: '',
    });
    const [showSetupPassword, setShowSetupPassword] = useState(false);
    const [showSetupConfirmPassword, setShowSetupConfirmPassword] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState(null);
    const [usernameValidation, setUsernameValidation] = useState(null);

    // User management state
    const [users, setUsers] = useState([]);
    const [userDialog, setUserDialog] = useState({ open: false, mode: 'create', user: null });
    const [userForm, setUserForm] = useState({
        username: '',
        password: '',
        email: '',
        role: 'user',
    });

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [cardVisible, setCardVisible] = useState(false);

    const checkSetupStatus = useCallback(async () => {
        try {
            setLoading(true);
            const setupStatus = await authService.checkSetup();
            setSetupRequired(setupStatus.setup_required);
            
            if (!setupStatus.setup_required && authService.isAuthenticated()) {
                if (onAuthChange) {
                    onAuthChange();
                }
            }
        } catch (err) {
            setError('Failed to check setup status');
        } finally {
            setLoading(false);
        }
    }, [onAuthChange]);

    useEffect(() => {
        setCardVisible(true);
        checkSetupStatus();
    }, [checkSetupStatus]);

    const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });
    const showError = (msg) => setSnackbar({ open: true, message: msg, severity: 'error' });
    const showSuccess = (msg) => setSnackbar({ open: true, message: msg, severity: 'success' });

    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError(null);
            await authService.login(loginForm);
            showSuccess('Login successful!');
            if (onAuthChange) {
                setTimeout(() => onAuthChange(), 1000);
            }
        } catch (err) {
            showError(err.response?.data?.detail || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    const handleSetupRoot = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError(null);
            
            if (setupForm.password !== setupForm.password_confirm) {
                showError('Passwords do not match');
                return;
            }

            await authService.setupRoot(setupForm);
            showSuccess('Root user setup successful!');
            if (onAuthChange) {
                setTimeout(() => onAuthChange(), 1000);
            }
        } catch (err) {
            showError(err.response?.data?.detail || 'Setup failed');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordValidation = async (password) => {
        if (password.length > 0) {
            try {
                const validation = await authService.validatePassword(password);
                setPasswordValidation(validation);
            } catch (err) {
                console.error('Password validation error:', err);
            }
        } else {
            setPasswordValidation(null);
        }
    };

    const handleUsernameValidation = async (username) => {
        if (username.length > 0) {
            try {
                const validation = await authService.validateUsername(username);
                setUsernameValidation(validation);
            } catch (err) {
                console.error('Username validation error:', err);
            }
        } else {
            setUsernameValidation(null);
        }
    };

    const handleSetupFormChange = (field, value) => {
        setSetupForm(prev => ({ ...prev, [field]: value }));
        
        if (field === 'password') {
            handlePasswordValidation(value);
        } else if (field === 'username') {
            handleUsernameValidation(value);
        }
    };

    const getPasswordStrengthColor = () => {
        if (!passwordValidation) return 'default';
        if (passwordValidation.strength_score >= 8) return 'success';
        if (passwordValidation.strength_score >= 6) return 'warning';
        return 'error';
    };

    const getPasswordStrengthLabel = () => {
        if (!passwordValidation) return '';
        if (passwordValidation.strength_score >= 8) return 'Strong';
        if (passwordValidation.strength_score >= 6) return 'Medium';
        return 'Weak';
    };

    const loadUsers = async () => {
        try {
            const userList = await authService.listUsers();
            setUsers(userList);
        } catch (err) {
            showError('Failed to load users');
        }
    };

    const handleCreateUser = async () => {
        try {
            await authService.createUser(userForm);
            setUserDialog({ open: false, mode: 'create', user: null });
            setUserForm({ username: '', password: '', email: '', role: 'user' });
            loadUsers();
            showSuccess('User created successfully');
        } catch (err) {
            showError(err.response?.data?.detail || 'Failed to create user');
        }
    };

    const handleUpdateUser = async () => {
        try {
            await authService.updateUser(userDialog.user.id, userForm);
            setUserDialog({ open: false, mode: 'edit', user: null });
            setUserForm({ username: '', password: '', email: '', role: 'user' });
            loadUsers();
            showSuccess('User updated successfully');
        } catch (err) {
            showError(err.response?.data?.detail || 'Failed to update user');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                await authService.deleteUser(userId);
                loadUsers();
                showSuccess('User deleted successfully');
            } catch (err) {
                showError(err.response?.data?.detail || 'Failed to delete user');
            }
        }
    };

    const steps = ['Login', 'Root Setup', 'User Management'];

    if (loading && !setupRequired) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #0a0e1a 0%, #1a1040 50%, #0f172a 100%)',
            p: 2
        }}>
            <Paper
                elevation={6}
                sx={{
                    p: 4,
                    maxWidth: 400,
                    width: '100%',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
                    textAlign: 'center',
                    opacity: cardVisible ? 1 : 0,
                    transform: cardVisible ? 'translateY(0)' : 'translateY(40px)',
                    transition: 'all 0.6s cubic-bezier(.4,2,.3,1)',
                    position: 'relative',
                    backgroundColor: '#111827',
                    border: '1px solid rgba(148, 163, 184, 0.1)',
                }}
            >
                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box
                        component="img"
                        src={Logo}
                        alt="NSCAT Logo"
                        sx={{
                            width: 80,
                            height: 80,
                            marginBottom: 2,
                            borderRadius: '50%',
                            objectFit: 'cover',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            transition: 'transform 0.3s ease-in-out',
                            '&:hover': {
                                transform: 'scale(1.05)',
                            }
                        }}
                    />
                    <Typography variant="h5" fontWeight="bold" gutterBottom align="center">
                        Network Security Configuration Auditing Tool
                    </Typography>
                </Box>
                {/* Snackbar for feedback */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
                {/* Login Form */}
                <Box component="form" onSubmit={handleLogin} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Username"
                            value={loginForm.username}
                            onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
                            margin="normal"
                            required
                        autoFocus
                            disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Person />
                                </InputAdornment>
                            ),
                        }}
                    />
                        <TextField
                            fullWidth
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            value={loginForm.password}
                            onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                            margin="normal"
                            required
                            disabled={loading}
                            InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOutlinedIcon />
                                </InputAdornment>
                            ),
                                endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                        aria-label="toggle password visibility"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                                ),
                            }}
                        />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <Button size="small" disabled sx={{ textTransform: 'none' }}>
                            Forgot password?
                        </Button>
                    </Box>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading}
                        sx={{ mt: 1, mb: 2, py: 1.2, fontWeight: 'bold', fontSize: '1rem' }}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                        {loading ? 'Logging in...' : 'Login'}
                        </Button>
                        {setupRequired && (
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => setActiveStep(1)}
                                sx={{ mb: 2 }}
                            >
                                Setup Root Administrator
                            </Button>
                        )}
                    </Box>
                {/* Root Setup Modal */}
                <Dialog open={activeStep === 1 && setupRequired} onClose={() => setActiveStep(0)} maxWidth="xs" fullWidth>
                    <DialogTitle>Setup Root Administrator</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            This will create the first administrator account for the system.
                        </Typography>
                        <TextField
                            fullWidth
                            label="Username"
                            value={setupForm.username}
                            onChange={(e) => handleSetupFormChange('username', e.target.value)}
                            margin="normal"
                            required
                            disabled={loading}
                            error={usernameValidation && !usernameValidation.is_valid}
                            helperText={usernameValidation && !usernameValidation.is_valid ? 
                                usernameValidation.errors.join(', ') : ''}
                        />
                        <TextField
                            fullWidth
                            label="Email (Optional)"
                            type="email"
                            value={setupForm.email}
                            onChange={(e) => handleSetupFormChange('email', e.target.value)}
                            margin="normal"
                            disabled={loading}
                        />
                        <TextField
                            fullWidth
                            label="Password"
                            type={showSetupPassword ? 'text' : 'password'}
                            value={setupForm.password}
                            onChange={(e) => handleSetupFormChange('password', e.target.value)}
                            margin="normal"
                            required
                            disabled={loading}
                            error={passwordValidation && !passwordValidation.is_valid}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowSetupPassword(!showSetupPassword)}
                                        edge="end"
                                    >
                                        {showSetupPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {passwordValidation && (
                            <Box sx={{ mt: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Chip 
                                        label={getPasswordStrengthLabel()}
                                        color={getPasswordStrengthColor()}
                                        size="small"
                                    />
                                    <Typography variant="caption">
                                        Score: {passwordValidation.strength_score}/10
                                    </Typography>
                                </Box>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={passwordValidation.strength_score * 10}
                                    color={getPasswordStrengthColor()}
                                    sx={{ height: 4, borderRadius: 2 }}
                                />
                                {passwordValidation.warnings.length > 0 && (
                                    <Typography variant="caption" color="warning.main" display="block">
                                        Warnings: {passwordValidation.warnings.join(', ')}
                                    </Typography>
                                )}
                            </Box>
                        )}
                        <TextField
                            fullWidth
                            label="Confirm Password"
                            type={showSetupConfirmPassword ? 'text' : 'password'}
                            value={setupForm.password_confirm}
                            onChange={(e) => handleSetupFormChange('password_confirm', e.target.value)}
                            margin="normal"
                            required
                            disabled={loading}
                            error={setupForm.password_confirm && setupForm.password !== setupForm.password_confirm}
                            helperText={setupForm.password_confirm && setupForm.password !== setupForm.password_confirm ? 
                                'Passwords do not match' : ''}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowSetupConfirmPassword(!showSetupConfirmPassword)}
                                        edge="end"
                                    >
                                        {showSetupConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setActiveStep(0)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSetupRoot}
                            variant="contained"
                            disabled={loading || 
                                !setupForm.username || 
                                !setupForm.password || 
                                !setupForm.password_confirm ||
                                setupForm.password !== setupForm.password_confirm ||
                                (passwordValidation && !passwordValidation.is_valid) ||
                                (usernameValidation && !usernameValidation.is_valid)}
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : 'Setup Root Administrator'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>

            {/* User Dialog */}
            <Dialog
                open={userDialog.open}
                onClose={() => setUserDialog({ open: false, mode: 'create', user: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {userDialog.mode === 'create' ? 'Create User' : 'Edit User'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Username"
                        value={userForm.username}
                        onChange={(e) => setUserForm(prev => ({ ...prev, username: e.target.value }))}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Password"
                        type="password"
                        value={userForm.password}
                        onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                        margin="normal"
                        required={userDialog.mode === 'create'}
                        helperText={userDialog.mode === 'edit' ? 'Leave empty to keep current password' : ''}
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Role</InputLabel>
                        <Select
                            value={userForm.role}
                            onChange={(e) => setUserForm(prev => ({ ...prev, role: e.target.value }))}
                            label="Role"
                        >
                            <MenuItem value="user">Auditor</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUserDialog({ open: false, mode: 'create', user: null })}>
                        Cancel
                    </Button>
                    <Button
                        onClick={userDialog.mode === 'create' ? handleCreateUser : handleUpdateUser}
                        variant="contained"
                    >
                        {userDialog.mode === 'create' ? 'Create' : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Login; 